import LoadingRow from '@/Components/Table/LoadingRow';
import NoRecordsRow from '@/Components/Table/NoRecordsRow';
import Title from '@/Components/Title/Title';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import type CheckModel from '@/Models/Check/CheckModel';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import ChecksStore from '@/Stores/ChecksStore';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { DobButton, DobCheckbox, DobInput, DobPagination, DobSelect, DobTableWrapper } from '@dedalusdiit/dob-components';
import TitleStyles from '@style/GenericTitle.module.scss';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel, getPaginationRowModel,
    getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Styles from './../../Style/GenericTable.module.scss';
import TableHeaderSortDirection from '@/Components/TableHeaderSortDirection/TableHeaderSortDirection';

interface ChecksProps {
    initialState: FilingCabinetItem;
}

const renderBooleanCell = (value: boolean | null | undefined) => {
    if (value === null || value === undefined) {
        return <>❌</>;
    }
    return <>{value ? '✅' : '❌'}</>;
};

const renderDateCell = (value: string | null | undefined) => {
    if (value == null) {
        return null;
    }
    return new Date(value).toLocaleDateString('nl-BE');
};

const CheckOverview = observer(({ initialState }: ChecksProps): React.ReactElement => {
    const fcContext = useFilingCabinetContext();

    const store = useMemo(() => new ChecksStore(new FilingCabinetStore(initialState, fcContext)), [initialState]);
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { pagination, sorting, nameFilter } = store.state;

    const convertGenderChar = (val: string): string => {
        if (val == null || val.trim() == '') {
            return 'Any';
        }

        return val == 'M' ? 'Male' : 'Female';
    };

    const columnHelper = useMemo(() => createColumnHelper<CheckModel>(), []);
    const columns = useMemo(
        () => [
            columnHelper.accessor('Id', {
                header: t('#'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('Name', {
                header: t('Name'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('Remarks', {
                header: t('Remarks'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('CopyRemarksToOrder', {
                header: t('CopyRemarksToOrder'),
                cell: (info) => renderBooleanCell(info.getValue()),
            }),
            columnHelper.accessor('CopyRemarksToPatient', {
                header: t('CopyRemarksToPatient'),
                cell: (info) => renderBooleanCell(info.getValue()),
            }),
            columnHelper.accessor('Global', {
                header: t('GlobalCheck'),
                cell: (info) => renderBooleanCell(info.getValue()),
            }),
            columnHelper.accessor('ValidFrom', {
                header: t('ValidFrom'),
                cell: (info) => renderDateCell(info.getValue()),
            }),
            columnHelper.accessor('ValidTo', {
                header: t('ValidTo'),
                cell: (info) => renderDateCell(info.getValue()),
            }),
            columnHelper.accessor('Gender', {
                header: t('Gender'),
                cell: (info) => t(convertGenderChar(info.getValue())),
            }),
            columnHelper.accessor('MinEffectiveAge', {
                header: t('MinEffectiveAge'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('MaxEffectiveAge', {
                header: t('MaxEffectiveAge'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('MinYearAge', {
                header: t('MinYearAge'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('MaxYearAge', {
                header: t('MaxYearAge'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('VisibleInTechnicianScreen', {
                header: t('VisibleInTechnicianScreen'),
                cell: (info) => renderBooleanCell(info.getValue()),
            }),
            columnHelper.accessor('RequiredToSaveAppointment', {
                header: t('RequiredToSaveAppointment'),
                cell: (info) => renderBooleanCell(info.getValue()),
            }),
        ],
        [],
    );

    const table = useReactTable({
        columns,
        data: store.filteredChecks,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: (updater) => {
            const newVal = typeof updater === 'function' ? updater(pagination) : updater;
            store.setPagination(newVal);
        },
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: (updater) => {
            const newVal = typeof updater === 'function' ? updater(sorting) : updater;
            store.setSorting(newVal);
        },
        state: {
            sorting,
            pagination,
        },
    });

    const openDetailCheck = useCallback((checkId: number) => {
        navigate(`/DetailCheck/${checkId}`);
    }, [navigate]);

    return (
        <>
            <Title titleName="Checks">
                <div className={TitleStyles.buttons}>
                    <DobButton className={TitleStyles.greenButton} color="silent" onClick={() => navigate('/DetailCheck')}>{t('AddNewCheck')}</DobButton>
                </div>
            </Title>

            {/* Filters */}
            <div className={Styles.tableOverviewFilters}>
                <DobInput icon="search" value={nameFilter} onValueChange={(filter) => store.set('nameFilter', filter)} placeholder={`${t('Search')} (${t('Name')})`} />
                <DobCheckbox label={t('FilterValidDates')} checked={store.state.validFilter} onCheckedChange={(checked) => store.set('validFilter', checked)} />
            </div>

            {/* Table */}
            <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                <div className={Styles.tabledataOverview}>
                    <DobTableWrapper color="primary">
                        <table>
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th key={header.id} {...{ onClick: header.column.getToggleSortingHandler() }} className={Styles.headerCell}>
                                                <div>
                                                    <span>{flexRender(t(header.column.columnDef.header as string), header.getContext())}</span>
                                                    <TableHeaderSortDirection sortDirection={header.column.getIsSorted()} />
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {store.loading && <LoadingRow colSpan={columns.length} />}
                                {!store.loading && table.getRowModel().rows.length === 0 && <NoRecordsRow colSpan={columns.length} />}
                                {table.getRowModel().rows.map((row) => (
                                    <tr key={row.id} onClick={() => openDetailCheck(row.original.Id)}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </DobTableWrapper>
                </div>
                {/* Footer */}
                <div className={Styles.tableFooter}>
                    <div className={Styles.footerActions}>
                        <DobPagination
                            currentPage={table.getState().pagination.pageIndex + 1}
                            recordsPerPage={table.getState().pagination.pageSize}
                            totalRecords={table.getRowCount()}
                            onChange={(page) => {
                                if (!store.loading) {
                                    const pageIndex = page - 1;
                                    table.setPageIndex(pageIndex);
                                }
                            }}
                        />
                        <div className={Styles.pageSizeSelect}>
                            <DobSelect
                                optionSelected={{ label: table.getState().pagination.pageSize.toString(), value: table.getState().pagination.pageSize }}
                                onChange={(selectedOption) => {
                                    if (selectedOption) {
                                        table.setPageSize(selectedOption.value);
                                    }
                                }}
                                options={[10, 25, 50, 100].map((pageSize) => ({
                                    label: pageSize.toString(),
                                    value: pageSize,
                                }))}
                                size="small"
                            />
                        </div>
                        <div>
                            <DobButton icon="arrow-clockwise" onClick={store.reload} color="silent">{t('Reload')}</DobButton>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CheckOverview;
